/**
 * A one-day pass starts and ends on the same calendar day of the tenant (America/Santo_Domingo, UTC-4 all year, no
 * daylight saving). The backend refuses any other (400 multi_day_not_allowed, SAM-996; pass_type_one_day for a Guest
 * or Client Booking pass, SAM-1183), so `start + 24 h`, which always lands on the next day, cannot be the end.
 */
const RD_TIME_ZONE = 'America/Santo_Domingo';

/** The calendar day (YYYY-MM-DD) an instant falls on in Santo Domingo. */
export const rdDay = (instant: Date | string): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: RD_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date(instant));

/** The last second (23:59:59) of the Santo Domingo day the start falls on, as an ISO instant. */
export const endOfRdDay = (start: Date | string): string => new Date(`${rdDay(start)}T23:59:59-04:00`).toISOString();

/** The «How long?» choice kept in the draft (InvitationContext `duration`). */
export interface PassDuration {
  mode?: 'one' | 'multiple';
  date?: string;
  startDate?: string;
  endDate?: string;
}

/**
 * start_date and end_date of the pass to create, from what the operator picked. One day: that day (or now when it
 * is today), ending the same Santo Domingo day. A range: the first day to the end of the last. A start in the past
 * (today at 00:00, a stale pick) moves to now + 5 minutes, the backend's minimum; with no choice it is now + 5 minutes.
 */
export const passWindow = (duration: PassDuration | null | undefined, now: Date = new Date()): { startDate: string; endDate: string } => {
  const earliest = new Date(now.getTime() + 5 * 60000);
  const valid = (iso?: string): Date | null => {
    const d = iso ? new Date(iso) : null;
    return d && !Number.isNaN(d.getTime()) ? d : null;
  };
  const chosen = valid(duration?.mode === 'multiple' ? duration.startDate : duration?.date);
  const start = chosen && chosen >= earliest ? chosen : earliest;
  const last = duration?.mode === 'multiple' ? valid(duration.endDate) : null;
  return { startDate: start.toISOString(), endDate: endOfRdDay(last && last > start ? last : start) };
};
